/**
 * @fileoverview A aba Inteligência do agente: o modelo e o raciocínio (só nos
 * GPT-5), o tamanho das respostas, o jeito de conversar (mensagens divididas,
 * imagens, áudios e vídeos), a espera pelo cliente terminar de digitar, quem
 * aparece como atendente (o usuário IA) e a foto.
 */

import React, { useRef, useState } from "react";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ImagePlus } from "lucide-react";

import api from "../../../../services/api";
import toastError from "../../../../errors/toastError";
import { i18n } from "../../../../translate/i18n";
import ModernSelect from "../../../../components/ModernSelect";
import useStyles from "../TriageManager/styles";
import SectionTitle from "../TriageManager/InfoTip";
import { AI_AGENT_MODELS, AI_AGENT_REASONING_LEVELS } from "../../aiAgentOptions";
import useAgentStyles from "./styles";
import AgentAvatar from "./AgentAvatar";

const t = (key) => i18n.t(`agentManager.editor.intelligence.${key}`);
const STYLES = ["concise", "balanced", "detailed"];
const SWITCHES = [
  ["splitMessages", "split"],
  ["processImages", "images"],
  ["processAudio", "audio"],
  ["processVideo", "video"],
];

const IntelligenceTab = ({ agent, form, onChange, readOnly, aiUsers, onPhotoSaved }) => {
  const classes = useStyles();
  const agentClasses = useAgentStyles();
  const photoInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const usesReasoning = String(form.model).startsWith("gpt-5");

  const select = (props) => <ModernSelect className={classes.field} size="small" fullWidth InputLabelProps={{ shrink: true }} disabled={readOnly} {...props} />;

  const uploadPhoto = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("profilePic", file);
      const { data } = await api.post(`/ai-agents/${agent.id}/profile-pic`, body, { headers: { "Content-Type": "multipart/form-data" } });
      onPhotoSaved(data.profilePicUrl);
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={agentClasses.columns}>
      <div className={agentClasses.column}>
        <div className={agentClasses.section}>
          <SectionTitle title={t("photo")} />
          <div className={agentClasses.photoRow}>
            <AgentAvatar name={form.name} profilePicUrl={agent?.profilePicUrl} size="large" />
            {agent ? (
              !readOnly && (
                <>
                  <input ref={photoInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={uploadPhoto} />
                  <button type="button" className={classes.btnSecondary} onClick={() => photoInputRef.current && photoInputRef.current.click()} disabled={uploading}>
                    {uploading ? <CircularProgress size={14} /> : <ImagePlus />}
                    {t("photoChange")}
                  </button>
                </>
              )
            ) : (
              <span className={agentClasses.muted}>{t("photoSaveFirst")}</span>
            )}
          </div>
        </div>
        <div className={agentClasses.section}>
          <SectionTitle title={t("model")} info={t("modelInfo")} />
          {select({
            value: form.model,
            onChange: (event) => onChange({ model: event.target.value }),
            renderValue: (value) => (AI_AGENT_MODELS.find((model) => model.value === value) || { label: value }).label,
            children: AI_AGENT_MODELS.map((model) => (
              <MenuItem key={model.value} value={model.value}>
                <div>
                  <div>{model.label}</div>
                  <div className={agentClasses.muted}>{model.short}</div>
                </div>
              </MenuItem>
            )),
          })}
        </div>
        <div className={agentClasses.section}>
          <SectionTitle title={t("reasoning")} info={t("reasoningInfo")} />
          {select({
            value: form.reasoningEffort,
            onChange: (event) => onChange({ reasoningEffort: event.target.value }),
            disabled: readOnly || !usesReasoning,
            helperText: usesReasoning ? undefined : t("reasoningOff"),
            renderValue: (value) => (AI_AGENT_REASONING_LEVELS.find((level) => level.value === value) || { label: value }).label,
            children: AI_AGENT_REASONING_LEVELS.map((level) => (
              <MenuItem key={level.value} value={level.value}>
                <div>
                  <div>{level.label}</div>
                  <div className={agentClasses.muted}>{level.desc}</div>
                </div>
              </MenuItem>
            )),
          })}
        </div>
        <div className={agentClasses.section}>
          <SectionTitle title={t("style")} />
          {select({
            value: form.responseStyle,
            onChange: (event) => onChange({ responseStyle: event.target.value }),
            children: STYLES.map((style) => (
              <MenuItem key={style} value={style}>
                {i18n.t(`agentManager.editor.intelligence.styles.${style}`)}
              </MenuItem>
            )),
          })}
        </div>
      </div>
      <div className={agentClasses.column}>
        <div className={agentClasses.section}>
          <SectionTitle title={t("behavior")} />
          {SWITCHES.map(([key, label]) => (
            <label key={key} className={agentClasses.switchRow}>
              {t(label)}
              <Switch size="small" color="primary" checked={form[key]} onChange={(event) => onChange({ [key]: event.target.checked })} disabled={readOnly} />
            </label>
          ))}
        </div>
        <div className={agentClasses.section}>
          <SectionTitle title={t("wait")} info={t("waitInfo")} />
          <TextField
            className={classes.field}
            variant="outlined"
            size="small"
            fullWidth
            type="number"
            value={form.responseDelay}
            onChange={(event) => onChange({ responseDelay: event.target.value })}
            inputProps={{ min: 3, max: 60 }}
            disabled={readOnly}
          />
        </div>
        <div className={agentClasses.section}>
          <SectionTitle title={t("aiUser")} info={t("aiUserInfo")} />
          {select({
            value: form.aiUserId || "",
            onChange: (event) => onChange({ aiUserId: event.target.value }),
            displayEmpty: true,
            children: [
              <MenuItem key="none" value="">
                {t("aiUserNone")}
              </MenuItem>,
              ...aiUsers.map((aiUser) => (
                <MenuItem key={aiUser.id} value={aiUser.id}>
                  {aiUser.name}
                </MenuItem>
              )),
            ],
          })}
        </div>
      </div>
    </div>
  );
};

export default IntelligenceTab;
